import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useRole } from '@/hooks/use-role';
import { useOrganization } from '@/contexts/OrganizationContext';
import { selectSetupProgress } from '@/features/onboarding/lib/steps';
import { TOP_HEADER_HEIGHT } from '@/features/onboarding/lib/setup-mode';

/**
 * The Back to setup strip (BLU-1376, handover 4.5): shown once, by AppLayout,
 * on the pages the wizard opens while the org is in setup mode. It sits
 * sticky right under TopHeader (at the header's own height, not the bumped
 * var), and AppLayout folds its height into `--top-header-height` so the
 * page's sticky surfaces pin below it.
 *
 * The step text reads the same cached onboarding status as the wizard (no
 * poll), and only for people who can read setup; everyone else gets the link
 * alone. Imported by file path, not through the feature barrel. Light only.
 */
export function BackToSetupBar() {
  const { t } = useTranslation('onboarding');
  const { can } = useRole();
  const { organization } = useOrganization();
  const canRead = can('org_settings.read');

  const statusQuery = trpc.productSync.getOnboardingStatus.useQuery(undefined, {
    enabled: canRead,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // A setup-mode org is setup-required, so `selectFlow` never reads the role
  // surfaces and their query is not needed here.
  const progress = selectSetupProgress({
    status: canRead ? statusQuery.data : undefined,
    setupRequired: organization?.setup_required === true,
    setupCompletedAt: organization?.setup_completed_at ?? null,
    isPureClient: false,
    surfacesLoading: false,
  });

  const { currentStep, stepIds, total, flow } = progress;
  const showStep = (flow === 'client' || flow === 'owner') && currentStep !== null;

  return (
    <nav
      aria-label={t('setupMode.barLabel')}
      className="sticky z-50 flex items-center gap-3 bg-primary-strong px-4 text-white sm:px-6 lg:px-8"
      style={{ top: TOP_HEADER_HEIGHT, height: 'var(--setup-bar-height)' }}
    >
      <Link
        to="/onboarding"
        className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-white px-3 text-sm font-medium text-primary-strong transition-colors hover:bg-white/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary-strong motion-reduce:transition-none"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('setupMode.backToSetup')}
      </Link>
      {showStep && currentStep ? (
        <span className="min-w-0 truncate text-sm text-white/85">
          {t('setupMode.barStep', {
            number: stepIds.indexOf(currentStep) + 1,
            total,
            label: t(`stepper.${currentStep}`),
          })}
        </span>
      ) : null}
    </nav>
  );
}
